import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import {
    Button,
    Card,
    Col,
    Empty,
    Progress,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileTextOutlined } from '@ant-design/icons';

import TrendIndicator, { TrendDirection } from './TrendIndicator';
import {
    ExecutiveRiskRow,
    HealthTrendPoint,
    RiskLevel,
    RiskTrendWindow,
    ScanTrendSnapshot,
} from './dashboardTypes';
import { HealthStatus } from '../../helpers/reports/areaHealthStatus';

const { Text } = Typography;

const RISK_LEVEL_LABEL: Record<RiskLevel, string> = {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    unknown: 'Unknown',
};

const RISK_LEVEL_COLOR: Record<RiskLevel, string> = {
    critical: 'red',
    high: 'orange',
    medium: 'gold',
    low: 'green',
    unknown: 'default',
};

const HEALTH_SCORE_COLOR: Record<number, string> = {
    1: 'red',
    2: 'orange',
    3: 'gold',
    4: 'lime',
    5: 'green',
};

const OVERALL_HEALTH_LABEL_BY_BAND: Record<number, string> = {
    1: 'Poor',
    2: 'Fair',
    3: 'Good',
    4: 'Great',
    5: 'Perfect',
};

type DashboardExecutiveViewProps = {
    overallSurfaceHealthScore: number | null;
    overallSurfaceHealthTrend: {
        direction: TrendDirection;
        delta: number | null;
        currentScore: number | null;
        previousScore: number | null;
    } | null;
    portfolioHealthSeries: HealthTrendPoint[];
    healthCounts: Record<HealthStatus, number>;
    scanTrendSnapshot: ScanTrendSnapshot | null;
    loadingScanTrend: boolean;
    executiveLoading: boolean;
    areaCount: number;
    riskRows: ExecutiveRiskRow[];
    onAreaNavigate: (areaId: number) => void;
    onOpenExecutiveReport: () => void;
    riskTrendWindow: RiskTrendWindow;
};

const buildSparklinePath = (
    points: HealthTrendPoint[],
    width: number,
    height: number,
    padding: number
): { linePath: string; areaPath: string } | null => {
    if (!Array.isArray(points) || points.length < 2) return null;

    const scores = points.map((point) => point.score);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    const safeMin = Number.isFinite(minScore) ? minScore : 0;
    const safeMax = Number.isFinite(maxScore) ? maxScore : 5;
    const range = safeMax - safeMin || 1;
    const innerWidth = width - padding * 2;
    const innerHeight = height - padding * 2;

    const coordinates = points.map((point, index) => {
        const x =
            padding + (points.length === 1 ? innerWidth / 2 : (innerWidth * index) / Math.max(points.length - 1, 1));
        const normalizedY = (point.score - safeMin) / range;
        const y = padding + innerHeight - normalizedY * innerHeight;
        return { x, y };
    });

    const linePath = coordinates
        .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
        .join(' ');

    const lastPoint = coordinates[coordinates.length - 1];
    const firstPoint = coordinates[0];
    const areaPath = `${linePath} L ${lastPoint.x.toFixed(2)} ${(height - padding).toFixed(2)} L ${firstPoint.x.toFixed(
        2
    )} ${(height - padding).toFixed(2)} Z`;

    return { linePath, areaPath };
};

const RISK_LEVEL_WEIGHT: Record<RiskLevel, number> = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
    unknown: 0,
};

const compareRiskRows = (a: ExecutiveRiskRow, b: ExecutiveRiskRow) => {
    const riskDiff = RISK_LEVEL_WEIGHT[b.riskLevel] - RISK_LEVEL_WEIGHT[a.riskLevel];
    if (riskDiff !== 0) return riskDiff;

    if (a.latestHealthScoreRounded !== null && b.latestHealthScoreRounded !== null) {
        if (a.latestHealthScoreRounded !== b.latestHealthScoreRounded) {
            return a.latestHealthScoreRounded - b.latestHealthScoreRounded;
        }
    } else if (a.latestHealthScoreRounded === null && b.latestHealthScoreRounded !== null) {
        return 1;
    } else if (a.latestHealthScoreRounded !== null && b.latestHealthScoreRounded === null) {
        return -1;
    }

    if (b.warningCount !== a.warningCount) return b.warningCount - a.warningCount;
    return a.areaName.localeCompare(b.areaName);
};

const flattenLeafRiskRows = (rows: ExecutiveRiskRow[]): ExecutiveRiskRow[] => {
    const flat: ExecutiveRiskRow[] = [];
    const visit = (row: ExecutiveRiskRow) => {
        if (row.children && row.children.length > 0) {
            row.children.forEach(visit);
            return;
        }
        flat.push(row);
    };
    rows.forEach(visit);
    return flat;
};

const getHealthLabel = (score: number | null) => {
    if (score === null) return 'No data';
    const rounded = Math.max(1, Math.min(5, Math.round(score)));
    return OVERALL_HEALTH_LABEL_BY_BAND[rounded];
};

const DashboardExecutiveView: React.FC<DashboardExecutiveViewProps> = ({
    overallSurfaceHealthScore,
    overallSurfaceHealthTrend,
    portfolioHealthSeries,
    healthCounts,
    scanTrendSnapshot,
    loadingScanTrend,
    executiveLoading,
    areaCount,
    riskRows,
    onAreaNavigate,
    onOpenExecutiveReport,
    riskTrendWindow,
}) => {
    const flattenedLeafRows = useMemo(() => flattenLeafRiskRows(riskRows).sort(compareRiskRows), [riskRows]);
    const trackedAreaCount = flattenedLeafRows.length || areaCount;
    const overallHealthLabel = getHealthLabel(overallSurfaceHealthScore);

    const attentionRows = useMemo(() => {
        const urgent = flattenedLeafRows.filter((row) => row.riskLevel === 'critical' || row.riskLevel === 'high');
        if (urgent.length > 0) return urgent.slice(0, 5);
        const secondary = flattenedLeafRows.filter((row) => row.riskLevel === 'medium' || row.riskLevel === 'unknown');
        if (secondary.length > 0) return secondary.slice(0, 5);
        return flattenedLeafRows.slice(0, 5);
    }, [flattenedLeafRows]);

    const benchmarkCoverageCount = useMemo(
        () => flattenedLeafRows.filter((row) => row.latestHealthScoreRounded !== null).length,
        [flattenedLeafRows]
    );
    const unscoredAreaCount = Math.max(trackedAreaCount - benchmarkCoverageCount, 0);
    const isCoverageGapMode = trackedAreaCount > 0 && benchmarkCoverageCount === 0;

    const latestScanAt = useMemo(() => {
        return (
            flattenedLeafRows
                .map((row) => row.latestAt)
                .filter((value): value is string => Boolean(value) && dayjs(value).isValid())
                .sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf())[0] || null
        );
    }, [flattenedLeafRows]);

    const freshnessWindowStart = useMemo(() => {
        const end = dayjs().endOf('day');
        return end.subtract(riskTrendWindow === 'week' ? 6 : 29, 'day').startOf('day');
    }, [riskTrendWindow]);

    const recentScanCount = useMemo(() => {
        return flattenedLeafRows.filter((row) => {
            if (!row.latestAt) return false;
            const parsed = dayjs(row.latestAt);
            return parsed.isValid() && !parsed.isBefore(freshnessWindowStart);
        }).length;
    }, [flattenedLeafRows, freshnessWindowStart]);
    const staleScanCount = Math.max(trackedAreaCount - recentScanCount, 0);

    const attentionCount = useMemo(() => {
        return flattenedLeafRows.filter((row) => row.riskLevel === 'critical' || row.riskLevel === 'high').length;
    }, [flattenedLeafRows]);
    const coverageGapRows = useMemo(() => {
        return flattenedLeafRows
            .filter((row) => row.latestHealthScoreRounded === null)
            .sort((a, b) => {
                const aTime = a.latestAt && dayjs(a.latestAt).isValid() ? dayjs(a.latestAt).valueOf() : -1;
                const bTime = b.latestAt && dayjs(b.latestAt).isValid() ? dayjs(b.latestAt).valueOf() : -1;
                if (aTime !== bTime) return bTime - aTime;
                return a.areaName.localeCompare(b.areaName);
            })
            .slice(0, 5);
    }, [flattenedLeafRows]);

    const topAttentionRow = attentionRows[0] || null;
    const topCoverageGapRow = coverageGapRows[0] || null;
    const benchmarkCoveragePercent = trackedAreaCount > 0 ? Math.round((benchmarkCoverageCount / trackedAreaCount) * 100) : 0;
    const scanActivityDeltaLabel =
        scanTrendSnapshot?.deltaPercent === null || scanTrendSnapshot?.deltaPercent === undefined
            ? 'No baseline'
            : `${scanTrendSnapshot.deltaPercent > 0 ? '+' : ''}${scanTrendSnapshot.deltaPercent.toFixed(1)}%`;
    const freshnessLabel = latestScanAt ? `Updated ${dayjs(latestScanAt).format('MMM D, YYYY')}` : 'No recent data';
    const attentionLeadLabel = topAttentionRow ? `Start: ${topAttentionRow.areaName}` : 'No urgent area';
    const coverageGapLeadLabel = topCoverageGapRow ? `Latest unscored: ${topCoverageGapRow.areaName}` : 'All tracked areas scored';
    const scanActivityWindowLabel = riskTrendWindow === 'week' ? 'Last 7d' : 'This month';
    const primaryCardTitle = isCoverageGapMode ? 'Coverage gaps' : 'Needs attention';
    const primaryCardValue = isCoverageGapMode ? unscoredAreaCount : attentionCount;
    const primaryCardCaption = isCoverageGapMode
        ? `${primaryCardValue === 1 ? 'area' : 'areas'} not scored`
        : `${primaryCardValue === 1 ? 'high-risk area' : 'high-risk areas'}`;
    const primaryCardNote = isCoverageGapMode ? coverageGapLeadLabel : attentionLeadLabel;
    const healthNarrative = useMemo(() => {
        if (trackedAreaCount === 0) {
            return 'No tracked areas yet.';
        }
        if (benchmarkCoverageCount === 0) {
            return 'No areas are scored yet. Add benchmark coverage to unlock portfolio health.';
        }
        if (attentionCount > 0 && topAttentionRow) {
            return `${attentionCount} ${attentionCount === 1 ? 'area needs' : 'areas need'} attention. Review ${topAttentionRow.areaName} first.`;
        }
        if (healthCounts.attention > 0) {
            return `${healthCounts.attention} ${healthCounts.attention === 1 ? 'area is' : 'areas are'} in warning range.`;
        }
        return 'No high-risk areas right now.';
    }, [attentionCount, benchmarkCoverageCount, healthCounts.attention, topAttentionRow, trackedAreaCount]);
    const healthCurve = useMemo(() => buildSparklinePath(portfolioHealthSeries, 320, 84, 6), [portfolioHealthSeries]);
    const hasHealthTrend = portfolioHealthSeries.length >= 2 && Boolean(healthCurve);
    const healthCurveWindowLabel = 'Health trend';
    const healthCurveWindowValue = riskTrendWindow === 'week' ? 'Last 7d' : 'Last 30d';
    const trendMetaLabel = riskTrendWindow === 'week' ? 'Trend vs prior 7d' : 'Trend vs prior 30d';
    const trendMetaValue =
        overallSurfaceHealthTrend?.delta === null || overallSurfaceHealthTrend?.delta === undefined
            ? 'No baseline'
            : `${overallSurfaceHealthTrend.delta > 0 ? '+' : ''}${overallSurfaceHealthTrend.delta.toFixed(2)}`;
    const overallHealthDisplay = overallSurfaceHealthScore !== null ? overallSurfaceHealthScore.toFixed(2) : null;
    const overallHealthScaleLabel =
        overallSurfaceHealthScore !== null ? 'out of 5' : isCoverageGapMode ? 'awaiting coverage' : 'awaiting score';
    const overallHealthTagLabel = overallSurfaceHealthScore !== null ? overallHealthLabel : isCoverageGapMode ? 'Unscored' : 'No data';
    const healthCurveCompactNote = isCoverageGapMode ? 'Need benchmark coverage first' : 'Need more benchmark days';
    const attentionSectionTitle = isCoverageGapMode ? 'Areas awaiting scoring' : 'Needs attention now';
    const attentionSectionNote = isCoverageGapMode ? 'Leaf areas without a benchmark KPI' : 'Highest-risk leaf areas first';
    const attentionSectionEmpty = isCoverageGapMode
        ? 'All tracked areas already have benchmark coverage.'
        : 'No high-priority areas to review.';
    const visibleAttentionRows = isCoverageGapMode ? coverageGapRows : attentionRows;

    const riskColumns = useMemo<ColumnsType<ExecutiveRiskRow>>(
        () => [
            {
                title: 'Area',
                dataIndex: 'areaName',
                key: 'area',
                render: (_: unknown, row) => (
                    <div className="dashboard-risk-area-cell">
                        <Space size={8} wrap>
                            <Text strong>{row.areaName}</Text>
                            {row.children && row.children.length > 0 ? <Tag color="blue">Parent average</Tag> : null}
                        </Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.latestAt ? `Last scan: ${dayjs(row.latestAt).format('MMM D, YYYY')}` : 'No latest scan'}
                        </Text>
                    </div>
                ),
            },
            {
                title: 'Focus',
                key: 'focus',
                width: 280,
                render: (_: unknown, row) => (
                    <Space direction="vertical" size={4}>
                        {row.latestHealthScoreRounded && row.latestHealthLabel ? (
                            <Space size={8} wrap>
                                <Tag color={HEALTH_SCORE_COLOR[row.latestHealthScoreRounded]} className="dashboard-health-label-tag">
                                    {row.latestHealthLabel}
                                </Tag>
                                <Text type="secondary">
                                    {row.latestHealthScore !== null ? `${row.latestHealthScore.toFixed(2)} / 5` : 'No score'}
                                </Text>
                            </Space>
                        ) : (
                            <Text type="secondary">No benchmark score</Text>
                        )}
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.worstMetricName
                                ? `Priority metric: ${row.worstMetricName}${row.worstMetricLabel ? ` (${row.worstMetricLabel})` : ''}`
                                : 'Priority metric unavailable'}
                        </Text>
                    </Space>
                ),
            },
            {
                title: `Trend (${riskTrendWindow === 'week' ? '7d' : '30d'})`,
                key: 'trend',
                width: 170,
                render: (_: unknown, row) => (
                    <Space size={6}>
                        <TrendIndicator trend={row.trend} positiveIsGood size="large" />
                        <Text>{row.trendDelta === null ? 'N/A' : `${row.trendDelta > 0 ? '+' : ''}${row.trendDelta.toFixed(2)}`}</Text>
                    </Space>
                ),
            },
            {
                title: 'Risk',
                dataIndex: 'riskLevel',
                key: 'riskLevel',
                width: 120,
                render: (riskLevel: RiskLevel) => (
                    <Tag className="dashboard-risk-level-tag" color={RISK_LEVEL_COLOR[riskLevel]}>
                        {RISK_LEVEL_LABEL[riskLevel]}
                    </Tag>
                ),
            },
            {
                title: 'Action',
                key: 'action',
                width: 140,
                align: 'right',
                render: (_: unknown, row) => (
                    <Button
                        size="small"
                        type="text"
                        icon={<FileTextOutlined />}
                        className="dashboard-risk-action-button"
                        onClick={(event) => {
                            event.stopPropagation();
                            onAreaNavigate(row.areaId);
                        }}
                    >
                        View report
                    </Button>
                ),
            },
        ],
        [onAreaNavigate, riskTrendWindow]
    );

    return (
        <>
            <Row gutter={[16, 16]} className="dashboard-executive-v2-hero">
                <Col xs={24} xl={10}>
                    <Card className="dashboard-executive-v2-card dashboard-executive-v2-health-card">
                        <div className="dashboard-executive-v2-health-stack">
                            <div className="dashboard-executive-v2-health-header">
                                <div>
                                    <div className="dashboard-executive-v2-kicker">Portfolio health</div>
                                </div>
                                <Button className="dashboard-executive-v2-hero-action" onClick={onOpenExecutiveReport}>
                                    Open focused report
                                </Button>
                            </div>

                            <div className="dashboard-executive-v2-health-score-row">
                                <div>
                                    {overallHealthDisplay ? (
                                        <div className="dashboard-executive-v2-health-score">{overallHealthDisplay}</div>
                                    ) : (
                                        <div className="dashboard-executive-v2-health-score-empty">Not scored</div>
                                    )}
                                    <div className="dashboard-executive-v2-health-scale">{overallHealthScaleLabel}</div>
                                </div>
                                <Tag className="dashboard-executive-v2-health-tag">{overallHealthTagLabel}</Tag>
                            </div>

                            <Text className="dashboard-executive-v2-health-summary">{healthNarrative}</Text>

                            {hasHealthTrend ? (
                                <div className="dashboard-executive-v2-health-curve">
                                    <div className="dashboard-executive-v2-health-curve-head">
                                        <span>{healthCurveWindowLabel}</span>
                                        <span>{healthCurveWindowValue}</span>
                                    </div>
                                    <svg
                                        className="dashboard-executive-v2-health-curve-svg"
                                        viewBox="0 0 320 84"
                                        preserveAspectRatio="none"
                                        aria-label={healthCurveWindowLabel}
                                    >
                                        <defs>
                                            <linearGradient id="dashboardExecutiveV2HealthCurveFill" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="rgba(255,255,255,0.34)" />
                                                <stop offset="100%" stopColor="rgba(255,255,255,0.03)" />
                                            </linearGradient>
                                        </defs>
                                        <path
                                            d={healthCurve!.areaPath}
                                            fill="url(#dashboardExecutiveV2HealthCurveFill)"
                                            stroke="none"
                                        />
                                        <path d={healthCurve!.linePath} fill="none" stroke="rgba(255,255,255,0.92)" strokeWidth="3" />
                                    </svg>
                                </div>
                            ) : (
                                <div className="dashboard-executive-v2-health-curve-compact">
                                    <span className="dashboard-executive-v2-health-curve-compact-label">
                                        {healthCurveWindowLabel}
                                    </span>
                                    <span className="dashboard-executive-v2-health-curve-compact-note">
                                        {healthCurveCompactNote}
                                    </span>
                                    <span className="dashboard-executive-v2-health-curve-compact-window">
                                        {healthCurveWindowValue}
                                    </span>
                                </div>
                            )}

                            <div className="dashboard-executive-v2-health-meta">
                                <div>
                                    <div className="dashboard-executive-v2-meta-label">{trendMetaLabel}</div>
                                    <div className="dashboard-executive-v2-meta-value dashboard-executive-v2-meta-value-inline">
                                        <TrendIndicator
                                            trend={overallSurfaceHealthTrend?.direction || 'none'}
                                            positiveIsGood
                                            size="large"
                                        />
                                        <span>{trendMetaValue}</span>
                                    </div>
                                </div>
                                <div>
                                    <div className="dashboard-executive-v2-meta-label">Benchmarked</div>
                                    <div className="dashboard-executive-v2-meta-value">
                                        {benchmarkCoverageCount}/{trackedAreaCount}
                                    </div>
                                </div>
                                <div>
                                    <div className="dashboard-executive-v2-meta-label">Latest scan</div>
                                    <div className="dashboard-executive-v2-meta-value">
                                        {latestScanAt ? dayjs(latestScanAt).format('MMM D, YYYY') : 'No data'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} xl={14}>
                    <Card title="At a glance" className="dashboard-executive-v2-card">
                        <div className="dashboard-executive-v2-glance-grid">
                            <div className="dashboard-executive-v2-glance-item">
                                <div className="dashboard-executive-v2-kicker">{primaryCardTitle}</div>
                                <div className="dashboard-executive-v2-mini-value">{primaryCardValue}</div>
                                <Text className="dashboard-executive-v2-glance-caption">{primaryCardCaption}</Text>
                                <Text type="secondary" className="dashboard-executive-v2-glance-note">
                                    {primaryCardNote}
                                </Text>
                            </div>

                            <div className="dashboard-executive-v2-glance-item">
                                <div className="dashboard-executive-v2-kicker">Data freshness</div>
                                <div className="dashboard-executive-v2-mini-value">
                                    {recentScanCount}/{trackedAreaCount}
                                </div>
                                <Text className="dashboard-executive-v2-glance-caption">
                                    {riskTrendWindow === 'week' ? 'scanned in 7d' : 'scanned in 30d'}
                                </Text>
                                <Text type="secondary" className="dashboard-executive-v2-glance-note">
                                    {freshnessLabel}
                                </Text>
                            </div>

                            <div className="dashboard-executive-v2-glance-item">
                                <div className="dashboard-executive-v2-kicker">Scan activity</div>
                                <div className="dashboard-executive-v2-mini-value">
                                    {loadingScanTrend ? '...' : scanTrendSnapshot ? scanTrendSnapshot.currentCount : '-'}
                                </div>
                                <Text className="dashboard-executive-v2-glance-caption">{scanActivityWindowLabel}</Text>
                                <Space size={6} wrap className="dashboard-executive-v2-glance-trend">
                                    <TrendIndicator trend={scanTrendSnapshot?.direction || 'none'} positiveIsGood size="large" />
                                    <Text type="secondary" className="dashboard-executive-v2-glance-note">
                                        {scanActivityDeltaLabel}
                                    </Text>
                                </Space>
                            </div>
                        </div>

                        {isCoverageGapMode ? (
                            <div className="dashboard-executive-v2-status-strip">
                                <div className="dashboard-executive-v2-status-pill">
                                    <span className="dashboard-executive-v2-status-pill-value" style={{ color: '#0f766e' }}>
                                        {benchmarkCoverageCount}
                                    </span>
                                    <span className="dashboard-executive-v2-status-pill-label">Benchmarked</span>
                                </div>
                                <div className="dashboard-executive-v2-status-pill">
                                    <span className="dashboard-executive-v2-status-pill-value" style={{ color: '#64748b' }}>
                                        {unscoredAreaCount}
                                    </span>
                                    <span className="dashboard-executive-v2-status-pill-label">Unscored</span>
                                </div>
                                <div className="dashboard-executive-v2-status-pill">
                                    <span className="dashboard-executive-v2-status-pill-value" style={{ color: '#2563eb' }}>
                                        {recentScanCount}
                                    </span>
                                    <span className="dashboard-executive-v2-status-pill-label">Fresh scans</span>
                                </div>
                                <div className="dashboard-executive-v2-status-pill">
                                    <span className="dashboard-executive-v2-status-pill-value" style={{ color: '#d97706' }}>
                                        {staleScanCount}
                                    </span>
                                    <span className="dashboard-executive-v2-status-pill-label">Older scans</span>
                                </div>
                            </div>
                        ) : (
                            <div className="dashboard-executive-v2-status-strip">
                                <div className="dashboard-executive-v2-status-pill">
                                    <span className="dashboard-executive-v2-status-pill-value" style={{ color: '#16a34a' }}>
                                        {healthCounts.healthy}
                                    </span>
                                    <span className="dashboard-executive-v2-status-pill-label">Healthy</span>
                                </div>
                                <div className="dashboard-executive-v2-status-pill">
                                    <span className="dashboard-executive-v2-status-pill-value" style={{ color: '#d97706' }}>
                                        {healthCounts.attention}
                                    </span>
                                    <span className="dashboard-executive-v2-status-pill-label">Warning</span>
                                </div>
                                <div className="dashboard-executive-v2-status-pill">
                                    <span className="dashboard-executive-v2-status-pill-value" style={{ color: '#dc2626' }}>
                                        {healthCounts['action-needed']}
                                    </span>
                                    <span className="dashboard-executive-v2-status-pill-label">Critical</span>
                                </div>
                                <div className="dashboard-executive-v2-status-pill">
                                    <span className="dashboard-executive-v2-status-pill-value" style={{ color: '#64748b' }}>
                                        {healthCounts['no-data']}
                                    </span>
                                    <span className="dashboard-executive-v2-status-pill-label">No data</span>
                                </div>
                            </div>
                        )}

                        <div className="dashboard-executive-v2-coverage-block">
                            <div className="dashboard-executive-v2-kicker">Benchmark coverage</div>
                            <Progress percent={benchmarkCoveragePercent} showInfo={false} strokeColor="#0f766e" />
                            <Text type="secondary" className="dashboard-executive-v2-glance-note">
                                {benchmarkCoverageCount}/{trackedAreaCount} areas benchmarked
                            </Text>
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24}>
                    <Card
                        title={attentionSectionTitle}
                        extra={
                            <Text type="secondary" className="dashboard-executive-v2-section-note">
                                {attentionSectionNote}
                            </Text>
                        }
                        className="dashboard-executive-v2-card"
                    >
                        {executiveLoading ? (
                            <div className="dashboard-loading">
                                <Spin />
                            </div>
                        ) : visibleAttentionRows.length === 0 ? (
                            <Empty description={attentionSectionEmpty} />
                        ) : (
                            <div className="dashboard-executive-v2-attention-list">
                                {visibleAttentionRows.map((area) => (
                                    <div
                                        key={area.areaId}
                                        className={`dashboard-executive-v2-attention-item ${
                                            isCoverageGapMode
                                                ? 'dashboard-executive-v2-attention-item-unscored'
                                                : `dashboard-executive-v2-attention-item-${area.riskLevel}`
                                        }`}
                                    >
                                        <div className="dashboard-executive-v2-attention-head">
                                            <div>
                                                <Space size={8} wrap>
                                                    <Text strong>{area.areaName}</Text>
                                                    <Tag color={isCoverageGapMode ? 'default' : RISK_LEVEL_COLOR[area.riskLevel]}>
                                                        {isCoverageGapMode ? 'Unscored' : RISK_LEVEL_LABEL[area.riskLevel]}
                                                    </Tag>
                                                </Space>
                                                <div className="dashboard-executive-v2-summary-text">
                                                    {isCoverageGapMode
                                                        ? [
                                                              'Benchmark KPI unavailable',
                                                              area.latestAt
                                                                  ? `Last scan: ${dayjs(area.latestAt).format('MMM D, YYYY')}`
                                                                  : 'No latest scan',
                                                          ].join(' | ')
                                                        : [
                                                              area.worstMetricName
                                                                  ? `Worst metric: ${area.worstMetricName}${
                                                                        area.worstMetricLabel ? ` (${area.worstMetricLabel})` : ''
                                                                    }`
                                                                  : null,
                                                              area.latestHealthLabel ? `KPI: ${area.latestHealthLabel}` : 'KPI unavailable',
                                                              area.latestAt
                                                                  ? `Last scan: ${dayjs(area.latestAt).format('MMM D, YYYY')}`
                                                                  : 'No latest scan',
                                                              area.warningCount > 0
                                                                  ? `${area.warningCount} warning${area.warningCount === 1 ? '' : 's'}`
                                                                  : 'No active warnings',
                                                          ]
                                                              .filter(Boolean)
                                                              .join(' | ')}
                                                </div>
                                            </div>

                                            <Button
                                                type="default"
                                                icon={<FileTextOutlined />}
                                                onClick={() => onAreaNavigate(area.areaId)}
                                            >
                                                View report
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>

            <Card
                title="Risk ranking"
                extra={<Text type="secondary">{riskTrendWindow === 'week' ? 'Window: Week' : 'Window: Month'}</Text>}
                className="dashboard-executive-v2-card dashboard-executive-v2-table-card"
            >
                {executiveLoading ? (
                    <div className="dashboard-loading">
                        <Spin />
                    </div>
                ) : trackedAreaCount === 0 ? (
                    <Empty description="No areas available for ranking." />
                ) : (
                    <Table
                        className="dashboard-risk-table dashboard-risk-table-v2"
                        rowKey="areaId"
                        columns={riskColumns}
                        dataSource={riskRows}
                        size="middle"
                        pagination={false}
                        rowClassName={(row) =>
                            row.riskLevel === 'critical'
                                ? 'dashboard-risk-row-critical'
                                : row.riskLevel === 'high'
                                  ? 'dashboard-risk-row-high'
                                  : ''
                        }
                        expandable={{
                            expandRowByClick: true,
                        }}
                    />
                )}
            </Card>
        </>
    );
};

export default DashboardExecutiveView;
